import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'components/Luxkit/Button/TextButton'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import moment from 'moment'
import React, { useCallback, useContext, useMemo } from 'react'
import { isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import AccommodationPackageTile from '../Accommodation/AccommodationPackageTile'
import AccommodationPackageTileContentArea from '../Accommodation/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from '../Accommodation/AccommodationPackageTileDetails'
import AccommodationPackageTileHeaderArea from '../Accommodation/AccommodationPackageTileHeaderArea'
import AccommodationPackageTilePricingArea from '../Accommodation/AccommodationPackageTilePricingArea'
import OfferPageLoyaltyRoomUpgradeCallOutBanner from '../OfferPageLoyaltyRoomUpgradeCallOutBanner'
import FlashHotelOptionPriceDetails from './FlashHotelOptionPriceDetails'
import useLoyaltyUserBenefit from 'loyalty/hooks/useLoyaltyUserBenefit'
import useLoyaltyOfferPageTargetRoomUpgrade from '../hooks/useLoyaltyOfferPageTargetRoomUpgrade'
import useLoyaltyOfferPageClearTargetRoomUpgrade from '../hooks/useLoyaltyOfferPageClearTargetRoomUpgrade'

interface Props {
  optionNumber: number;
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  checkIn?: string;
  duration: number;
  flightDestination?: App.Airport;
  previewMode: boolean;
  isLoyaltyEnabled: boolean;
  showOptionLabel?: boolean;
  availableForPurchase?: boolean;
}

function FlashHotelRateOption(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    duration,
    optionNumber,
    flightDestination,
    previewMode,
    isLoyaltyEnabled,
    showOptionLabel = true,
    availableForPurchase,
  } = props

  const { partnership } = useContext(GeoContext)
  const {
    package: pkg,
  } = packageView

  const showPartnership =
    (packageView.hotelAvailable &&
      !!partnership?.hasEarn &&
      offer.canEarnPartnershipPoints)

  const showModal = useContext(ModalContext)
  const showPrices = (packageView.capacityValid && packageView.hotelAvailable) || previewMode
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const {
    selectedTargetUpgradePackage,
  } = useLoyaltyOfferPageTargetRoomUpgrade(pkg)

  const {
    inclusions: targetUpgradeInclusions,
    bonusInclusions: targetUpgradeBonusInclusions,
    luxPlusInclusions: targetUpgradeLuxPlusInclusions,
  } = useOfferInclusions(selectedTargetUpgradePackage, { checkIn: checkInMoment, checkOut: checkOutMoment })

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const loyaltyRoomUpgradeBenefitDetails = useLoyaltyUserBenefit('hotel_upgrade')
  const hasLoyaltyRoomUpgradesAvailable = !!loyaltyRoomUpgradeBenefitDetails?.remaining

  useLoyaltyOfferPageClearTargetRoomUpgrade(packageView)

  return (
    <AccommodationPackageTile data-testid={pkg.uniqueKey}>
      {isLoyaltyEnabled && hasLoyaltyRoomUpgradesAvailable && <AccommodationPackageTileHeaderArea>
        <OfferPageLoyaltyRoomUpgradeCallOutBanner packageView={packageView} />
      </AccommodationPackageTileHeaderArea>}
      <AccommodationPackageTileContentArea>
        <AccommodationPackageTileDetails
          packageNumber={showOptionLabel ? optionNumber : undefined}
            // @ts-expect-error not App.HotelPackage
          roomPackage={packageView.package}
          selectedLoyaltyTargetUpgradePackage={selectedTargetUpgradePackage}
        >
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            <Group direction="vertical" gap={4} noDisplayWhenEmpty>
              {availableForPurchase && offer.property && <>
                {!selectedTargetUpgradePackage && pkg.roomRate && <FlashCancellationPolicies
                  checkInDate={checkIn}
                  propertyTimezoneOffset={offer.property.timezoneOffset}
                  cancellationPolicy={pkg.roomRate.cancellationPolicy}
                />}
                {selectedTargetUpgradePackage?.roomRate && <FlashCancellationPolicies
                  checkInDate={checkIn}
                  propertyTimezoneOffset={offer.property.timezoneOffset}
                  cancellationPolicy={selectedTargetUpgradePackage.roomRate.cancellationPolicy}
                />}
              </>}
              {!!showPartnership && pkg.partnership && <OfferPackagePartnershipLabel
                offer={offer}
                price={packageView.hotelPrice}
                packagePartnership={pkg.partnership}
              />}
            </Group>
          </Group>
        </AccommodationPackageTileDetails>
        <div>
          <LargeContentToggle
            maxHeight={208}
            showMoreText="View more inclusions"
            showLessText="View less inclusions"
          >
            <VerticalSpacer gap={8}>
              {!selectedTargetUpgradePackage && <>
                {!!luxPlusInclusions.length && <LuxPlusInclusions
                  luxPlusInclusions={luxPlusInclusions}
                  accommodationOffer={offer}
                  hideUpsellModal
                />}
                <InclusionsSection inclusions={inclusions} bonusInclusions={bonusInclusions} />
              </>}
              {!!selectedTargetUpgradePackage && <>
                {!!targetUpgradeLuxPlusInclusions.length && <LuxPlusInclusions
                  luxPlusInclusions={targetUpgradeLuxPlusInclusions}
                  accommodationOffer={offer}
                  hideUpsellModal
                />}
                <InclusionsSection inclusions={targetUpgradeInclusions} bonusInclusions={targetUpgradeBonusInclusions} />
              </>}
            </VerticalSpacer>
          </LargeContentToggle>
        </div>
      </AccommodationPackageTileContentArea>
      <AccommodationPackageTilePricingArea>
        <WalledContent
          enforced={offer.walledGarden}
          wall={(
            <Group fullHeight direction="vertical" verticalAlign="end">
              <TextButton kind="secondary" onClick={onSignUpClick}>Sign up to view exclusive rates</TextButton>
            </Group>
          )}
        >
          {showPrices && <FlashHotelOptionPriceDetails
            packageView={packageView}
            flightDestination={flightDestination}
          />}
        </WalledContent>
      </AccommodationPackageTilePricingArea>
    </AccommodationPackageTile>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  previewMode: appState.system.previewMode,
  isLoyaltyEnabled: getIsLoyaltyEnabled(appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
}))(FlashHotelRateOption)
